import type { Client } from "./_internal/client/index";
import type { RequestOptions } from "./base-client";
import { handleApiError, ServerError, GptCoreError } from "./errors";
import {
  streamMessage,
  type StreamMessageChunk,
  type StreamOptions,
} from "./streaming";
import type { PaginatedResponse, PaginationLinks } from "./pagination";

/**
 * Shape of API response envelope (from openapi-ts generated client)
 */
interface ApiResponseEnvelope {
  data?: unknown;
  links?: PaginationLinks;
  meta?: Record<string, unknown>;
  [key: string]: unknown;
}

/**
 * Response shape for list endpoints that return `{ data: T[], meta: M }`.
 * Used by rawGetList to preserve pagination metadata from custom controllers.
 */
export interface ListResponse<T, M = { count: number }> {
  data: T[];
  meta: M;
}

/**
 * Shape of stream response from client.post with parseAs: 'stream'
 */
interface StreamResponseEnvelope {
  data?: ReadableStream | Response;
  response?: Response;
  [key: string]: unknown;
}

/**
 * Build headers for SDK requests.
 * Merges base headers with per-request overrides and idempotency keys.
 */
export function buildHeaders(
  getHeaders: () => Record<string, string>,
  options?: RequestOptions,
): Record<string, string> {
  const headers: Record<string, string> = { ...getHeaders() };
  if (options?.headers) {
    Object.assign(headers, options.headers);
  }
  if (options?.idempotencyKey) {
    headers["Idempotency-Key"] = options.idempotencyKey;
  }
  return headers;
}

/**
 * Enrich an error from the hey-api client with the HTTP response status code.
 *
 * hey-api returns `{ error, request, response }` on non-2xx when throwOnError
 * is false. The raw methods destructure only `{ data, error }`, losing the
 * response object. Without the HTTP status, `handleApiError` can't classify
 * the error correctly (e.g., 404 becomes NetworkError instead of NotFoundError).
 */
function enrichError(error: unknown, result: unknown): unknown {
  if (
    typeof error === "object" &&
    error !== null &&
    typeof result === "object" &&
    result !== null
  ) {
    const res = (result as Record<string, unknown>).response;
    if (res && typeof res === "object" && "status" in res) {
      (error as Record<string, unknown>).statusCode = (
        res as { status: number }
      ).status;
    }
  }
  return error;
}

/**
 * RequestBuilder provides a type-safe way to execute SDK requests
 * with consistent header merging, error handling, retry, and unwrapping.
 */
export class RequestBuilder {
  constructor(
    private clientInstance: Client,
    private getHeaders: () => Record<string, string>,
    private unwrap: <T>(d: unknown) => T,
    private requestWithRetry: <T>(fn: () => Promise<T>) => Promise<T>,
    private getBaseUrl: () => string | undefined = () => undefined,
  ) {}

  /**
   * Assert that a baseUrl is configured on this client instance.
   * Raw requests use relative URL paths that require a base URL to resolve.
   * Without one, the fetch runtime throws an opaque "Invalid URL" error.
   * @throws {Error} with a descriptive message when baseUrl is missing.
   */
  private assertBaseUrl(): void {
    const url = this.getBaseUrl();
    if (!url || url.trim() === "") {
      throw new Error(
        "GptAdmin client is not initialized with a baseUrl. " +
          "Pass baseUrl to the constructor: new GptAdmin({ baseUrl: 'https://api.example.com', apiKey: '...' })",
      );
    }
  }

  /** Get auth headers for manual requests (used by streaming extensions) */
  getRequestHeaders(): Record<string, string> {
    return this.getHeaders();
  }

  /**
   * Execute a generated SDK function with full middleware pipeline.
   * Handles headers, retry, unwrapping, and error conversion.
   */
  async execute<TResponse>(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    fn: (...args: any[]) => Promise<any>,
    params: Record<string, unknown>,
    options?: RequestOptions,
  ): Promise<TResponse> {
    const headers = buildHeaders(this.getHeaders, options);

    try {
      const result = await this.requestWithRetry(() =>
        fn({
          client: this.clientInstance,
          headers,
          ...params,
          ...(options?.signal && { signal: options.signal }),
        }),
      );
      const { data, error } = result as Record<string, unknown>;

      if (error) {
        throw enrichError(error, result);
      }

      return this.unwrap<TResponse>((data as Record<string, unknown>)?.data);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Execute a delete operation that returns true on success.
   */
  async executeDelete(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    fn: (...args: any[]) => Promise<any>,
    params: Record<string, unknown>,
    options?: RequestOptions,
  ): Promise<true> {
    const headers = buildHeaders(this.getHeaders, options);

    try {
      const result = await this.requestWithRetry(() =>
        fn({
          client: this.clientInstance,
          headers,
          ...params,
          ...(options?.signal && { signal: options.signal }),
        }),
      );
      const { error } = result as Record<string, unknown>;

      if (error) {
        throw enrichError(error, result);
      }

      return true;
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Execute a raw GET request to a custom (non-generated) endpoint.
   * Used for endpoints implemented as custom Phoenix controllers.
   */
  async rawGet<TResponse>(
    url: string,
    options?: RequestOptions,
  ): Promise<TResponse> {
    this.assertBaseUrl();
    const headers = buildHeaders(this.getHeaders, options);

    try {
      const result = await this.requestWithRetry(() =>
        this.clientInstance.get({
          url,
          headers,
          ...(options?.signal && { signal: options.signal }),
        }),
      );
      const { data, error } = result as Record<string, unknown>;
      if (error) {
        throw handleApiError(enrichError(error, result));
      }
      return this.unwrap<TResponse>((data as Record<string, unknown>)?.data);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Execute a raw GET request for list endpoints that return `{ data: T[], meta: M }`.
   * Unlike rawGet which strips the envelope, this preserves pagination metadata.
   * Used for custom controller endpoints that return paginated lists.
   */
  async rawGetList<T, M = { count: number }>(
    url: string,
    options?: RequestOptions,
  ): Promise<ListResponse<T, M>> {
    this.assertBaseUrl();
    const headers = buildHeaders(this.getHeaders, options);

    try {
      const result = await this.requestWithRetry(() =>
        this.clientInstance.get({
          url,
          headers,
          ...(options?.signal && { signal: options.signal }),
        }),
      );
      const { data, error } = result as Record<string, unknown>;
      if (error) {
        throw handleApiError(enrichError(error, result));
      }
      const envelope = data as ApiResponseEnvelope;
      const items = this.unwrap<T[]>(envelope.data) || [];
      return { data: items, meta: (envelope.meta ?? {}) as M };
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Execute a raw multipart/form-data POST request.
   * Used for file upload endpoints (custom Phoenix controllers).
   * Content-Type is omitted from headers so the browser sets the correct
   * multipart boundary automatically from the FormData body.
   */
  async rawPostMultipart<TResponse>(
    url: string,
    body: FormData,
    options?: RequestOptions,
  ): Promise<TResponse> {
    this.assertBaseUrl();
    const headers = buildHeaders(this.getHeaders, options);
    // Tell mergeHeaders to remove Content-Type so the runtime auto-detects
    // multipart/form-data with the correct boundary from the FormData body.
    // Setting null (not delete) is required — mergeHeaders treats null as
    // "delete this key", while delete just leaves the default intact.
    headers["Content-Type"] = null as unknown as string;

    const result = await this.requestWithRetry(() =>
      this.clientInstance.post({
        url,
        headers,
        body,
        bodySerializer: null,
        ...(options?.signal && { signal: options.signal }),
      }),
    );
    const { data, error } = result as Record<string, unknown>;
    if (error) {
      throw handleApiError(enrichError(error, result));
    }
    // Custom endpoints return plain JSON (not JSON:API), so return data
    // directly without unwrapping a nested `data` property.
    return data as TResponse;
  }

  /**
   * Execute a raw POST request to a custom (non-generated) endpoint.
   * Used for endpoints implemented as custom Phoenix controllers.
   */
  async rawPost<TResponse>(
    url: string,
    body?: unknown,
    options?: RequestOptions,
  ): Promise<TResponse> {
    this.assertBaseUrl();
    const headers = buildHeaders(this.getHeaders, options);

    try {
      const result = await this.requestWithRetry(() =>
        this.clientInstance.post({
          url,
          headers,
          ...(body !== undefined && { body }),
          ...(options?.signal && { signal: options.signal }),
        }),
      );
      const { data, error } = result as Record<string, unknown>;
      if (error) {
        throw handleApiError(enrichError(error, result));
      }
      return this.unwrap<TResponse>((data as Record<string, unknown>)?.data);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Execute a raw PATCH request to a custom (non-generated) endpoint.
   * Used for endpoints implemented as custom Phoenix controllers.
   */
  async rawPatch<TResponse>(
    url: string,
    body?: unknown,
    options?: RequestOptions,
  ): Promise<TResponse> {
    this.assertBaseUrl();
    const headers = buildHeaders(this.getHeaders, options);

    try {
      const result = await this.requestWithRetry(() =>
        this.clientInstance.patch({
          url,
          headers,
          ...(body !== undefined && { body }),
          ...(options?.signal && { signal: options.signal }),
        }),
      );
      const { data, error } = result as Record<string, unknown>;
      if (error) {
        throw handleApiError(enrichError(error, result));
      }
      return this.unwrap<TResponse>((data as Record<string, unknown>)?.data);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Execute a raw DELETE request to a custom (non-generated) endpoint.
   */
  async rawDelete<TResponse = void>(
    url: string,
    options?: RequestOptions,
  ): Promise<TResponse> {
    this.assertBaseUrl();
    const headers = buildHeaders(this.getHeaders, options);

    try {
      const result = await this.requestWithRetry(() =>
        this.clientInstance.delete({
          url,
          headers,
          ...(options?.signal && { signal: options.signal }),
        }),
      );
      const { data, error } = result as Record<string, unknown>;
      if (error) {
        throw handleApiError(enrichError(error, result));
      }
      return this.unwrap<TResponse>((data as Record<string, unknown>)?.data);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  /**
   * Create a paginated fetcher function for listAll operations.
   * Encapsulates the pattern of calling a generated SDK function with pagination params.
   *
   * @param fn - The generated SDK function (e.g., getAgents)
   * @param queryBuilder - Function that builds the query object with page params
   * @param options - Request options (headers, signal, etc.)
   * @returns A fetcher function for use with paginateToArray
   */
  createPaginatedFetcher<T>(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    fn: (...args: any[]) => Promise<any>,
    queryBuilder: (page: number, pageSize: number) => Record<string, unknown>,
    options?: RequestOptions,
  ): (page: number, pageSize: number) => Promise<PaginatedResponse<T>> {
    return async (
      page: number,
      pageSize: number,
    ): Promise<PaginatedResponse<T>> => {
      const headers = buildHeaders(this.getHeaders, options);
      const result = await this.requestWithRetry(() =>
        fn({
          client: this.clientInstance,
          headers,
          ...(options?.signal && { signal: options.signal }),
          ...queryBuilder(page, pageSize),
        }),
      );
      const { data, error } = result as Record<string, unknown>;
      if (error) {
        throw handleApiError(enrichError(error, result));
      }
      const envelope = data as ApiResponseEnvelope;
      const items = this.unwrap<T[]>(envelope.data) || [];
      return { data: items, links: envelope.links };
    };
  }

  /**
   * Make a streaming POST request through the client instance,
   * ensuring all interceptors (auth, events, API version, etc.) fire.
   *
   * Uses the client's `post()` method with `parseAs: 'stream'` so the
   * request/response interceptors execute, then wraps the stream body
   * into an SSE message iterator.
   */
  async streamRequest(
    url: string,
    body: unknown,
    options?: RequestOptions,
    streamOptions?: StreamOptions,
  ): Promise<AsyncIterableIterator<StreamMessageChunk>> {
    const headers = buildHeaders(this.getHeaders, options);
    // Override Accept for SSE streaming
    headers["Accept"] = "text/event-stream";

    const result = await this.clientInstance.post({
      url,
      headers,
      body: JSON.stringify({
        data: { type: "chat-message", attributes: body },
      }),
      parseAs: "stream",
      ...(options?.signal && { signal: options.signal }),
    });

    // The result shape with default responseStyle 'fields' is { data, response }
    const envelope = result as StreamResponseEnvelope;
    const streamBody = envelope.data ?? result;
    const response = envelope.response;

    // If we got a response object, check status
    if (response && !response.ok) {
      throw new ServerError(`Stream request failed: ${response.status}`, {
        statusCode: response.status,
      });
    }

    // If the result is a ReadableStream, wrap it in a Response for streamMessage
    if (streamBody instanceof ReadableStream) {
      const syntheticResponse = new Response(streamBody, {
        headers: { "Content-Type": "text/event-stream" },
      });
      return streamMessage(syntheticResponse, {
        signal: options?.signal,
        ...streamOptions,
      });
    }

    // If we somehow got a Response back directly
    if (streamBody instanceof Response) {
      if (!streamBody.ok) {
        throw new ServerError(`Stream request failed: ${streamBody.status}`, {
          statusCode: streamBody.status,
        });
      }
      return streamMessage(streamBody, {
        signal: options?.signal,
        ...streamOptions,
      });
    }

    throw new GptCoreError("Unexpected stream response format", {
      code: "stream_error",
    });
  }

  /**
   * Make a streaming GET request through the client instance.
   * Used for subscribing to SSE event streams (e.g., execution streaming).
   */
  async streamGetRequest(
    url: string,
    options?: RequestOptions,
    streamOptions?: StreamOptions,
  ): Promise<AsyncIterableIterator<StreamMessageChunk>> {
    const headers = buildHeaders(this.getHeaders, options);
    headers["Accept"] = "text/event-stream";

    const result = await this.clientInstance.get({
      url,
      headers,
      parseAs: "stream",
      ...(options?.signal && { signal: options.signal }),
    });

    const envelope = result as StreamResponseEnvelope;
    const streamBody = envelope.data ?? result;
    const response = envelope.response;

    if (response && !response.ok) {
      throw new ServerError(`Stream request failed: ${response.status}`, {
        statusCode: response.status,
      });
    }

    if (streamBody instanceof ReadableStream) {
      const syntheticResponse = new Response(streamBody, {
        headers: { "Content-Type": "text/event-stream" },
      });
      return streamMessage(syntheticResponse, {
        signal: options?.signal,
        ...streamOptions,
      });
    }

    if (streamBody instanceof Response) {
      if (!streamBody.ok) {
        throw new ServerError(`Stream request failed: ${streamBody.status}`, {
          statusCode: streamBody.status,
        });
      }
      return streamMessage(streamBody, {
        signal: options?.signal,
        ...streamOptions,
      });
    }

    throw new GptCoreError("Unexpected stream response format", {
      code: "stream_error",
    });
  }
}
